import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TrainerLayout from '../../components/layout/TrainerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, User, ClipboardList, Activity, TrendingUp, FileText, Video as VideoIcon,
  Loader2, Save, Plus, Trash2, Mail, Phone, Dumbbell, Calendar, Target, Trash, X,
} from 'lucide-react';
import { needsConversion, convertToMp4 } from '../../utils/videoConverter';
import MacroPositionDialog from '../../components/admin/MacroPositionDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainerClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [assignedPlans, setAssignedPlans] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [progress, setProgress] = useState(null);
  const [myPlans, setMyPlans] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plan');

  // Edit basic info
  const [editForm, setEditForm] = useState({ name: '', phone: '' });
  const [reposOpen, setReposOpen] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);

  // Assign plan
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [planToAssign, setPlanToAssign] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Notes
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Trainer video
  const [videoFile, setVideoFile] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [convertingVideo, setConvertingVideo] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Filter sessions
  const [exerciseFilter, setExerciseFilter] = useState('');

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchAll = async () => {
    try {
      const [detailRes, sessionsRes, notesRes, progressRes, plansRes] = await Promise.all([
        axios.get(`${API}/trainer/clients/${clientId}`),
        axios.get(`${API}/trainer/clients/${clientId}/sessions`),
        axios.get(`${API}/trainer/clients/${clientId}/notes`),
        axios.get(`${API}/trainer/clients/${clientId}/progress`),
        axios.get(`${API}/trainer/plans`),
      ]);
      setClient(detailRes.data.client);
      setAssignedPlans(detailRes.data.assigned_plans || []);
      setSessions(sessionsRes.data.sessions || []);
      setNotes(notesRes.data.notes || []);
      setProgress(progressRes.data);
      setMyPlans(plansRes.data.my_plans || []);
      setEditForm({
        name: detailRes.data.client?.name || '',
        phone: detailRes.data.client?.phone || ''
      });
      setVideoTitle(detailRes.data.client?.trainer_video_title || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar el cliente');
      if (error.response?.status === 403) navigate('/trainer/clients');
    } finally {
      setInitialLoading(false);
    }
  };

  // ---------- DATOS ----------
  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      await axios.put(`${API}/trainer/clients/${clientId}`, editForm);
      toast.success('Datos guardados');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSavingInfo(false);
    }
  };

  // ---------- PLANES ----------
  const handleAssignPlan = async () => {
    if (!planToAssign) {
      toast.error('Selecciona un plan');
      return;
    }
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/trainer/clients/${clientId}/assign-plan`, { plan_id: planToAssign });
      toast.success(res.data.message);
      setShowAssignDialog(false);
      setPlanToAssign('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignPlan = async (planId, planName) => {
    if (!window.confirm(`¿Quitar el plan "${planName}" a ${client.name}?`)) return;
    try {
      await axios.post(`${API}/trainer/clients/${clientId}/unassign-plan`, { plan_id: planId });
      toast.success('Plan retirado');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // ---------- NOTAS ----------
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Escribe una nota');
      return;
    }
    setSavingNote(true);
    try {
      await axios.post(`${API}/trainer/clients/${clientId}/notes`, { content: newNote });
      toast.success('Nota añadida');
      setNewNote('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Borrar esta nota?')) return;
    try {
      await axios.delete(`${API}/trainer/clients/${clientId}/notes/${noteId}`);
      toast.success('Nota eliminada');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // ---------- VIDEO PERSONAL ----------
  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error('Video demasiado grande (máx 200MB)');
      return;
    }
    setVideoFile(file);
    toast.success(`Video seleccionado: ${file.name}`);
  };

  const handleUploadTrainerVideo = async () => {
    if (!videoFile) return;
    let fileToUpload = videoFile;
    try {
      const mustConvert = await needsConversion(videoFile);
      if (mustConvert) {
        setConvertingVideo(true);
        setConversionProgress(0);
        toast.info('Convirtiendo video a formato web...');
        fileToUpload = await convertToMp4(videoFile, (p) => setConversionProgress(p));
        setConvertingVideo(false);
        toast.success('Conversión completada');
      }
    } catch (e) {
      console.error(e);
      setConvertingVideo(false);
    }

    setUploadingVideo(true);
    setUploadProgress(0);
    try {
      // Subir en chunks de 1.5MB para no chocar con límites de proxy
      const CHUNK_SIZE = 1.5 * 1024 * 1024;
      const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);
      const uploadId = `welc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileToUpload.size);
        const chunk = fileToUpload.slice(start, end);
        const fd = new FormData();
        fd.append('chunk', chunk);
        fd.append('chunk_index', i.toString());
        fd.append('total_chunks', totalChunks.toString());
        fd.append('upload_id', uploadId);
        fd.append('filename', fileToUpload.name);
        await axios.post(
          `${API}/trainer/clients/${clientId}/welcome-video/upload-chunk`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
      }

      const res = await axios.post(
        `${API}/trainer/clients/${clientId}/welcome-video/finalize`,
        {
          upload_id: uploadId,
          filename: fileToUpload.name,
          total_chunks: totalChunks,
          title: videoTitle || `Mensaje para ${client.name}`,
        },
        { timeout: 180000 }
      );
      setUploadProgress(100);
      toast.success(`Vídeo guardado (${res.data.size_mb || '?'}MB)`);
      setVideoFile(null);
      setVideoTitle('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error subiendo vídeo');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  // ---------- DERIVADOS ----------
  const filteredSessions = useMemo(() => {
    if (!exerciseFilter) return sessions;
    const f = exerciseFilter.toLowerCase();
    return sessions.filter(s => (s.exercises || []).some(ex => (ex.exercise_name || '').toLowerCase().includes(f)));
  }, [sessions, exerciseFilter]);

  const adherence = useMemo(() => {
    if (!sessions.length) return 0;
    const last30Days = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = sessions.filter(s => new Date(s.completed_at).getTime() > last30Days);
    return Math.min(100, Math.round((recent.length / 12) * 100)); // asume 3/sem * 4 = 12/mes
  }, [sessions]);

  if (initialLoading) {
    return (
      <TrainerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </TrainerLayout>
    );
  }

  if (!client) {
    return (
      <TrainerLayout>
        <p className="text-muted-foreground">Cliente no disponible</p>
      </TrainerLayout>
    );
  }

  return (
    <TrainerLayout>
      <div className="space-y-6 animate-fade-in" data-testid="trainer-client-detail">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/trainer/clients')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Volver
            </Button>
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {client.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{client.name}</h1>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{client.email}</span>
                {client.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.phone}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Badge variant="outline" className="text-xs">
              {assignedPlans.length} plan{assignedPlans.length !== 1 ? 'es' : ''} asignado{assignedPlans.length !== 1 ? 's' : ''}
            </Badge>
            <Badge variant="outline" className="text-xs">{sessions.length} sesiones</Badge>
            {/* Toggle Modo Descanso Consciente */}
            <button
              data-testid="toggle-rest-mode"
              onClick={async () => {
                const isOn = (client.experimental_features || []).includes('rest_mode');
                try {
                  await axios.put(`${API}/trainer/clients/${client.id}/rest-mode`, { enabled: !isOn });
                  setClient(c => ({ ...c, experimental_features: !isOn
                    ? [...(c.experimental_features || []), 'rest_mode']
                    : (c.experimental_features || []).filter(f => f !== 'rest_mode') }));
                  toast.success(`Descanso consciente ${!isOn ? 'activado' : 'desactivado'}`);
                } catch (e) {
                  toast.error('No se pudo actualizar');
                }
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                (client.experimental_features || []).includes('rest_mode')
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25'
                  : 'bg-zinc-900 border-zinc-700 text-muted-foreground hover:border-zinc-500'
              }`}
              title="Activa el descanso guiado entre series para este cliente"
            >
              {(client.experimental_features || []).includes('rest_mode')
                ? '🟢 Descanso consciente: ON'
                : '⚪ Descanso consciente: OFF'}
            </button>
          </div>
        </div>

        {/* Métricas resumen */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Sesiones totales</p>
              <p className="text-2xl font-bold text-foreground tabular-nums">{progress?.total_sessions || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Volumen total</p>
              <p className="text-2xl font-bold text-foreground tabular-nums">{progress?.total_volume_kg || 0} kg</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Adherencia (30d)</p>
              <p className="text-2xl font-bold text-foreground tabular-nums">{adherence}%</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Notas</p>
              <p className="text-2xl font-bold text-foreground tabular-nums">{notes.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900 grid grid-cols-3 md:grid-cols-6 w-full">
            <TabsTrigger value="plan" data-testid="tab-plan"><ClipboardList className="h-4 w-4 mr-1" />Plan</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions"><Activity className="h-4 w-4 mr-1" />Sesiones</TabsTrigger>
            <TabsTrigger value="progress" data-testid="tab-progress"><TrendingUp className="h-4 w-4 mr-1" />Progreso</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes"><FileText className="h-4 w-4 mr-1" />Notas</TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data"><User className="h-4 w-4 mr-1" />Datos</TabsTrigger>
            <TabsTrigger value="video" data-testid="tab-video"><VideoIcon className="h-4 w-4 mr-1" />Vídeo</TabsTrigger>
          </TabsList>

          {/* TAB PLAN */}
          <TabsContent value="plan" className="mt-4 space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4 text-primary" />Plan asignado</CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-amber-400 border-amber-400/50 hover:bg-amber-400/10"
                    data-testid="reposition-client-btn"
                    onClick={() => setReposOpen(true)}
                  >
                    <Target className="h-4 w-4 mr-1" />Reposicionar
                  </Button>
                  <Button size="sm" onClick={() => setShowAssignDialog(true)} data-testid="assign-plan-btn">
                    <Plus className="h-4 w-4 mr-1" />Asignar plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {assignedPlans.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Aún no tiene plan. Asígnale uno de los tuyos.</p>
                ) : (
                  <div className="space-y-2">
                    {assignedPlans.map(plan => (
                      <div key={plan.id} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded">
                        <div>
                          <p className="font-medium text-foreground text-sm">{plan.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {plan.objective && <span className="text-xs text-muted-foreground">{plan.objective}</span>}
                            <Badge variant="outline" className="text-xs">{plan.weeks_count} semanas</Badge>
                            {!plan.is_mine && <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-400">GuardiansFit</Badge>}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10"
                          onClick={() => handleUnassignPlan(plan.id, plan.name)} data-testid={`unassign-${plan.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB SESIONES */}
          <TabsContent value="sessions" className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Input placeholder="Filtrar por ejercicio..." value={exerciseFilter}
                onChange={(e) => setExerciseFilter(e.target.value)}
                className="bg-zinc-900/50 border-zinc-800 max-w-xs" data-testid="exercise-filter" />
              <span className="text-xs text-muted-foreground">{filteredSessions.length} sesiones</span>
            </div>
            {filteredSessions.length === 0 ? (
              <Card className="bg-card border-dashed">
                <CardContent className="py-12 text-center">
                  <Activity className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm">Aún no hay sesiones registradas</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredSessions.slice(0, 100).map((s) => (
                  <Card key={s.id || s.completed_at} className="bg-card border-border">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {s.completed_at ? new Date(s.completed_at).toLocaleString('es-ES') : 'Sin fecha'}
                          </span>
                          {s.session_name && <Badge variant="outline" className="text-xs">{s.session_name}</Badge>}
                        </div>
                        {s.mood && <Badge variant="outline" className="text-xs">{s.mood}</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        {(s.exercises || []).slice(0, 5).map((ex, i) => {
                          const sets = ex.sets || [];
                          const targetReps = ex.target_reps || '?';
                          // Los sets guardan la carga en "load" (kg), no en "weight"
                          // Si no hay reps por serie (el cliente no las registra), mostramos las reps target del plan
                          const sumary = sets.map(st => {
                            const load = st.load ?? st.weight ?? 0;
                            const reps = st.reps || targetReps;
                            const rpe = st.rpe ? ` @${st.rpe}` : '';
                            return `${load}kg×${reps}${rpe}`;
                          }).join(' · ');
                          return (
                            <div key={i} className="flex items-center gap-2 truncate">
                              <Dumbbell className="h-3 w-3 flex-shrink-0" />
                              <span className="font-medium text-foreground">{ex.exercise_name}</span>
                              <span className="truncate">{sumary}</span>
                            </div>
                          );
                        })}
                        {(s.exercises || []).length > 5 && (
                          <p className="italic">+{s.exercises.length - 5} ejercicios más</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB PROGRESO */}
          <TabsContent value="progress" className="mt-4 space-y-3">
            {(!progress?.exercises || progress.exercises.length === 0) ? (
              <Card className="bg-card border-dashed">
                <CardContent className="py-12 text-center">
                  <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm">Sin datos de progreso aún</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {progress.exercises.slice(0, 20).map((ex) => (
                  <Card key={ex.name} className="bg-card border-border">
                    <CardContent className="p-4">
                      <p className="font-medium text-foreground text-sm mb-2">{ex.name}</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">Carga máx</p>
                          <p className="text-base font-bold text-primary tabular-nums">{ex.max_weight}kg</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Volumen</p>
                          <p className="text-base font-bold text-foreground tabular-nums">{Math.round(ex.total_volume)}kg</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Sesiones</p>
                          <p className="text-base font-bold text-foreground tabular-nums">{ex.sessions_count}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB NOTAS */}
          <TabsContent value="notes" className="mt-4 space-y-3">
            <Card className="bg-card border-border">
              <CardContent className="p-4 space-y-2">
                <Label className="text-xs">Nueva nota sobre {client.name}</Label>
                <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
                  rows={3} placeholder="Anota observaciones, ajustes, sensaciones..."
                  className="bg-zinc-900/50 border-zinc-800" data-testid="note-input" />
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleAddNote} disabled={savingNote} data-testid="save-note-btn">
                    {savingNote ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                    Añadir nota
                  </Button>
                </div>
              </CardContent>
            </Card>
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-6">Sin notas todavía</p>
            ) : (
              <div className="space-y-2">
                {notes.map((n) => (
                  <Card key={n.id} className="bg-card border-border">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm text-foreground whitespace-pre-wrap">{n.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {n.author_name || (n.author_type === 'trainer' ? 'Tú' : 'Admin')} · {n.created_at ? new Date(n.created_at).toLocaleString('es-ES') : ''}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400"
                          onClick={() => handleDeleteNote(n.id)} data-testid={`delete-note-${n.id}`}>
                          <Trash className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB DATOS */}
          <TabsContent value="data" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base">Datos básicos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Nombre</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                    className="bg-zinc-900/50 border-zinc-800" data-testid="edit-name" />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))}
                    className="bg-zinc-900/50 border-zinc-800" placeholder="Opcional" data-testid="edit-phone" />
                </div>
                <Button onClick={handleSaveInfo} disabled={savingInfo} data-testid="save-info-btn">
                  {savingInfo ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Guardar
                </Button>

                {/* Onboarding readonly */}
                {client.onboarding_data && (
                  <div className="mt-6 pt-4 border-t border-zinc-800">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Onboarding del cliente</p>
                    <div className="bg-zinc-900/50 rounded p-3 text-xs space-y-1">
                      {Object.entries(client.onboarding_data || {}).slice(0, 20).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <span className="text-muted-foreground capitalize min-w-[140px]">{k.replace(/_/g, ' ')}:</span>
                          <span className="text-foreground">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB VIDEO */}
          <TabsContent value="video" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <VideoIcon className="h-4 w-4 text-primary" />
                  Mensaje en vídeo para {client.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {client.trainer_video_url && (
                  <div className="bg-zinc-900/50 rounded p-3">
                    <p className="text-xs text-muted-foreground mb-2">Vídeo actual:</p>
                    <p className="text-sm text-foreground">{client.trainer_video_title || 'Sin título'}</p>
                  </div>
                )}
                <div>
                  <Label>Título del mensaje</Label>
                  <Input value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)}
                    placeholder={`Mensaje para ${client.name}`}
                    className="bg-zinc-900/50 border-zinc-800" data-testid="video-title" />
                </div>
                <div>
                  <Label>Vídeo (mp4, mov, hevc...)</Label>
                  <Input type="file" accept="video/*" onChange={handleVideoSelect}
                    className="bg-zinc-900/50 border-zinc-800" data-testid="video-input" />
                  {videoFile && <p className="text-xs text-muted-foreground mt-1">{videoFile.name} ({(videoFile.size/1024/1024).toFixed(1)}MB)</p>}
                </div>

                {convertingVideo && (
                  <div>
                    <div className="w-full bg-zinc-800 rounded h-2">
                      <div className="bg-amber-500 h-2 rounded transition-all" style={{ width: `${conversionProgress}%` }} />
                    </div>
                    <p className="text-xs text-amber-400 mt-1">Convirtiendo... {conversionProgress}%</p>
                  </div>
                )}
                {uploadingVideo && (
                  <div>
                    <div className="w-full bg-zinc-800 rounded h-2">
                      <div className="bg-primary h-2 rounded transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Subiendo... {uploadProgress}%</p>
                  </div>
                )}

                <Button onClick={handleUploadTrainerVideo} disabled={!videoFile || uploadingVideo || convertingVideo}
                  data-testid="upload-video-btn">
                  {uploadingVideo || convertingVideo ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Guardar vídeo
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog: Asignar plan */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Asignar plan a {client.name}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Selecciona uno de tus planes propios para asignárselo al cliente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {myPlans.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  Aún no tienes planes propios. <Button variant="link" className="h-auto p-0" onClick={() => navigate('/trainer/plans/new')}>Crear plan</Button>
                </p>
              ) : (
                <Select value={planToAssign} onValueChange={setPlanToAssign}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-plan">
                    <SelectValue placeholder="Selecciona un plan..." />
                  </SelectTrigger>
                  <SelectContent>
                    {myPlans.filter(p => !assignedPlans.find(ap => ap.id === p.id)).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name} {p.objective ? `· ${p.objective}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancelar</Button>
              <Button onClick={handleAssignPlan} disabled={!planToAssign || assigning} data-testid="confirm-assign-btn">
                {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Asignar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <MacroPositionDialog
          open={reposOpen}
          onClose={() => setReposOpen(false)}
          clientId={clientId}
          clientName={client?.name || 'cliente'}
          planId={assignedPlans && assignedPlans.length === 1 ? assignedPlans[0].id : null}
          currentMesocycle={progress?.current_mesocycle || 1}
          currentWeek={progress?.current_week || 1}
          currentSession={progress?.current_session || 1}
          sessionsCompleted={progress?.sessions_completed || 0}
          onSuccess={() => window.location.reload()}
        />
      </div>
    </TrainerLayout>
  );
};

export default TrainerClientDetailPage;
